import { getGetTenderOpportunityUnreadCountQueryKey, useGetTenderOpportunityUnreadCount } from '@cdf/api-client-react';

import { useTenantEntity } from '@/components/access/tenant-entity-provider';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

export const UNREAD_POLL_MS = 60_000;

/**
 * Unread strong/possible tender opportunities for the caller. The navigation
 * badge, the Opportunities tab and the inbox share this query key, so marking
 * an item seen (which invalidates it) updates every badge.
 */
export function useTenderOpportunityUnreadCount() {
  const access = useTenantEntity();
  const enabled = access.status === 'ready' && access.hasPermission('entity.records.read');
  const scopeKey = [access.selected?.tenantId, access.selected?.entityId].join(':');
  return useGetTenderOpportunityUnreadCount({ query: {
    queryKey: [...getGetTenderOpportunityUnreadCountQueryKey(), scopeKey], enabled, retry: false, refetchInterval: UNREAD_POLL_MS
  } });
}

export const unreadBadgeLabel = (count: number) => count > 99 ? '99+' : String(count);

/** Unread tender badge for the Tenders navigation entry; renders nothing when there is nothing new. */
export function TenderNavigationBadge({ collapsed = false }: { collapsed?: boolean }) {
  const unread = useTenderOpportunityUnreadCount();
  const count = unread.data?.count ?? 0;
  if (count <= 0) return null;
  const label = count + (count === 1 ? ' new tender opportunity' : ' new tender opportunities');
  if (collapsed) {
    return <span role="status" aria-label={label} className="bg-primary absolute top-1 right-1 size-2 rounded-full" />;
  }
  return <Badge role="status" aria-label={label} className={cn('ml-auto h-5 min-w-5 justify-center px-1.5 text-[11px] tabular-nums')}>{unreadBadgeLabel(count)}</Badge>;
}
